<template>
  <div ref="lottieRef" :class="[ns.b()]" :style="style" />
</template>
<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import lottieWeb from 'lottie-web'
import { useId, useNamespace } from '@xzx-design/hooks'
import { lottieEmits, lottieProps } from './lottie'

import type { CSSProperties } from 'vue'

import type {
  AnimationConfigWithData,
  AnimationConfigWithPath,
  AnimationItem,
  RendererType,
} from 'lottie-web'

let lottieName = useId()
const lottieRef: any = ref(null)
const lottie = ref<AnimationItem | null>(null)

defineOptions({
  name: 'XzxLottie',
})

const props = defineProps(lottieProps)
const emits = defineEmits(lottieEmits)

const ns = useNamespace('lottie')

const initLottie = () => {
  lottieName = useId()
  const lottieOptions:
    | AnimationConfigWithPath<RendererType>
    | AnimationConfigWithData<RendererType> = {
    container: lottieRef.value,
    rendererSettings:
      props.renderer === 'html'
        ? ({
            className: ns.e('html'),
          } as any)
        : {},
    renderer: props.renderer as RendererType,
    loop: props.loop,
    autoplay: props.autoplay,
    animationData: props.data,
    name: lottieName.value,
  }
  lottie.value = lottieWeb.loadAnimation(lottieOptions)
}

const style = computed<CSSProperties>(() => ({
  width: typeof props.width === 'number' ? `${props.width}px` : props.width,
  height: typeof props.height === 'number' ? `${props.height}px` : props.height,
}))

/**
 * 继承lottie-web的方法
 * @description https://www.npmjs.com/package/lottie-web#lottie-has-several-global-methods-that-will-affect-all-animations
 *
 */
const _defineExpose = {
  play: () => {
    lottie.value?.play()
  },
  pause: () => {
    lottie.value?.pause()
  },
  stop: () => {
    lottie.value?.stop()
  },
  destroy: () => {
    lottie.value?.destroy(lottieName.value)
  },
  goToAndPlay: (goToAndPlay: any, isFrame: any) => {
    lottie.value?.goToAndPlay(goToAndPlay, isFrame, lottieName.value)
  },
  goToAndStop: (goToAndStop: any, isFrame: any) => {
    lottie.value?.goToAndStop(goToAndStop, isFrame, lottieName.value)
  },
  setSpeed: (speed: any) => {
    lottie.value?.setSpeed(speed)
  },
  setDirection: (direction: any) => {
    lottie.value?.setDirection(direction)
  },
  playSegments: (segments: any, forceFlag: any) => {
    lottie.value?.playSegments(segments, forceFlag)
  },
  resetSegments: (forceFlag: boolean) => {
    lottie.value?.resetSegments(forceFlag)
  },
  setSubframe: (subframeEnabled: any) => {
    lottie.value?.setSubframe(subframeEnabled)
  },
  resize: () => {
    lottie.value?.resize()
  },
  hide: () => {
    lottie.value?.hide()
  },
  show: () => {
    lottie.value?.show()
  },
  getDuration: (inFrames: any) => lottie.value?.getDuration(inFrames),
  addEventListener: lottie.value?.addEventListener,
  removeEventListener: lottie.value?.removeEventListener,
}

const update = () => {
  unmount()
  initLottie()
}

const unmount = () => {
  try {
    lottie.value?.destroy(lottieName.value)
  } catch {}
  try {
    lottie.value?.removeEventListener('drawnFrame')
  } catch {}
}

// 解构排除 progress
const filteredProps = computed(() => {
  const { progress, ...rest } = props
  return rest
})

watch(filteredProps, update)

watch(
  () => [props.progress, lottie.value],
  (val) => {
    if (lottie.value && val[0] !== undefined) {
      const total = lottie.value.totalFrames
      const progress = Number(val[0]) * total

      lottie.value?.[props.autoplay ? 'goToAndPlay' : 'goToAndStop'](
        progress,
        true,
        lottieName.value
      )

      lottie.value.addEventListener('drawnFrame', (r: any) => {
        emits('update:progress', r.currentTime / total)
      })
    }
  },
  {
    immediate: true,
  }
)

onMounted(() => initLottie())

onUnmounted(() => {
  unmount()
})

defineExpose(_defineExpose)
</script>
